import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNotification, DeleteConfirmationModal } from '../common';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const EmailTemplatesSettings = () => {
  const [templates, setTemplates] = useState([]);
  const [templateTypes, setTemplateTypes] = useState([]);
  const [activeTab, setActiveTab] = useState('manual');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const { showNotification } = useNotification();
  
  // Variables dropdown setup
  const commonVariables = ['lead_name', 'first_name', 'lead_email', 'user_name', 'company_name', 'country'];
  
  // Insert a variable at the cursor position
  const insertVariable = (variable) => {
    // Insert the variable placeholder at cursor
    const variableText = `{{${variable}}}`;
    
    // If in HTML source mode, handle differently
    if (showHtmlSource) {
      // Get textarea and insert at cursor
      const textarea = document.getElementById('html-source');
      if (textarea) {
        const startPos = textarea.selectionStart;
        const endPos = textarea.selectionEnd;
        
        const before = formData.body.substring(0, startPos);
        const after = formData.body.substring(endPos);
        
        setFormData({
          ...formData,
          body: before + variableText + after
        });
        
        // Set cursor position after inserted variable
        setTimeout(() => {
          textarea.focus();
          const newCursorPos = startPos + variableText.length;
          textarea.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
      }
    } else {
      // For Quill editor, use the API to insert text at current selection
      const quill = document.querySelector('.ql-editor');
      if (quill) {
        // Insert variable with styling
        const range = window.getSelection().getRangeAt(0);
        const span = document.createElement('span');
        span.className = 'template-variable';
        span.textContent = variableText;
        span.style.backgroundColor = '#e5f0ff';
        span.style.padding = '0 4px';
        span.style.borderRadius = '3px';
        span.style.color = '#1e40af';
        span.style.fontWeight = '500';
        
        range.deleteContents();
        range.insertNode(span);
        
        // Move cursor to after the inserted variable
        range.setStartAfter(span);
        range.setEndAfter(span);
        window.getSelection().removeAllRanges();
        window.getSelection().addRange(range);
        
        // Update the Quill content from DOM
        setFormData(prev => ({
          ...prev,
          body: quill.innerHTML
        }));
      }
    }
  };
  
  // Rich text editor configuration
  const modules = {
    toolbar: {
      container: [
        ['bold', 'italic', 'underline'],
        [{'list': 'ordered'}, {'list': 'bullet'}],
        ['link', 'clean'],
        [{'color': []}, {'background': []}],
        [{'size': ['small', false, 'large']}]
      ],
      handlers: {}
    }
  };
  
  const formats = [
    'bold', 'italic', 'underline',
    'list', 'bullet',
    'link',
    'color', 'background',
    'size'
  ];
  
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    body: '',
    is_default: false,
    variables: [],
    template_type: 'manual',
    country_code: ''
  });
  
  // State for showing HTML source code
  const [showHtmlSource, setShowHtmlSource] = useState(false);
  
  // Load templates and template types on component mount
  useEffect(() => {
    fetchTemplates();
    fetchTemplateTypes();
  }, []);
  
  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('token');
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      const res = await axios.get('/api/email/templates', config);
      
      setTemplates(res.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching email templates:', err);
      setError('Failed to load email templates');
      setLoading(false);
    }
  };
  
  const fetchTemplateTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      const res = await axios.get('/api/email/template-types', config);
      setTemplateTypes(res.data);
    } catch (err) {
      console.error('Error fetching template types:', err);
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  const handleVariablesChange = (e) => {
    // Split input by commas and convert to array, removing whitespace
    const variablesArray = e.target.value
      .split(',')
      .map(v => v.trim())
      .filter(v => v !== '');
    
    setFormData({
      ...formData,
      variables: variablesArray
    });
  };
  
  const openAddModal = (templateType = 'manual') => {
    setFormData({
      name: '',
      subject: '',
      body: '',
      is_default: false,
      variables: [],
      template_type: templateType,
      country_code: ''
    });
    setModalMode('add');
    setShowModal(true);
  };
  
  const openEditModal = (template) => {
    setCurrentTemplate(template);
    setFormData({
      name: template.name,
      subject: template.subject,
      body: template.body,
      is_default: template.is_default,
      variables: template.variables || [],
      template_type: template.template_type || 'manual',
      country_code: template.country_code || ''
    });
    setModalMode('edit');
    setShowModal(true);
  };
  
  const closeModal = () => {
    setShowModal(false);
    setCurrentTemplate(null);
    setShowHtmlSource(false); // Reset HTML source view
  };
  
  const openPreviewModal = (template) => {
    setPreviewTemplate(template);
    setShowPreviewModal(true);
  };
  
  const closePreviewModal = () => {
    setShowPreviewModal(false);
    setPreviewTemplate(null);
  };
  
  // Handle escape key press to close modals
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        if (showModal) closeModal();
        if (showPreviewModal) closePreviewModal();
      }
    };
    
    window.addEventListener('keydown', handleEsc);
    
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [showModal, showPreviewModal]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      const token = localStorage.getItem('token');
      
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };
      
      // Create or update the template
      if (modalMode === 'add') {
        await axios.post('/api/email/templates', formData, config);
        showNotification('Template created successfully', 'success');
      } else {
        await axios.put(`/api/email/templates/${currentTemplate.id}`, formData, config);
        showNotification('Template updated successfully', 'success');
      }
      
      // Refresh templates
      fetchTemplates();
      
      // Close the modal
      closeModal();
    } catch (err) {
      console.error('Error saving template:', err);
      showNotification(err.response?.data?.message || 'Failed to save template', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDelete = async (templateId) => {
    setTemplateToDelete(templateId);
    setShowDeleteConfirm(true);
  };
  
  const confirmDelete = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      const token = localStorage.getItem('token');
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      await axios.delete(`/api/email/templates/${templateToDelete}`, config);
      
      showNotification('Template deleted successfully', 'success');
      
      // Refresh templates
      fetchTemplates();
      setShowDeleteConfirm(false);
    } catch (err) {
      console.error('Error deleting template:', err);
      showNotification(err.response?.data?.message || 'Failed to delete template', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  const handleMakeDefault = async (templateId) => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      const token = localStorage.getItem('token');
      
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };
      
      // Find the template in our state
      const template = templates.find(t => t.id === templateId);
      
      if (!template) {
        throw new Error('Template not found');
      }
      
      // Update the template to be default
      await axios.put(
        `/api/email/templates/${templateId}`,
        { ...template, is_default: true },
        config
      );
      
      showNotification('Default template updated successfully', 'success');
      
      // Refresh templates
      fetchTemplates();
    } catch (err) {
      console.error('Error updating default template:', err);
      showNotification(err.response?.data?.message || 'Failed to update default template', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Filter templates by the active tab/type
  const filteredTemplates = templates.filter(template => 
    template.template_type === activeTab
  );

  // Get type name for display
  const getTypeName = (typeId) => {
    const type = templateTypes.find(t => t.id === typeId);
    return type ? type.name : typeId;
  };
  
  // Get country name for display
  const getCountryName = (countryCode) => {
    const countryMap = {
      'NO': 'Norway',
      'SE': 'Sweden',
      'DK': 'Denmark',
      'FI': 'Finland',
      'DE': 'Germany',
      'GB': 'United Kingdom',
      'US': 'United States',
      'FR': 'France',
      'ES': 'Spain',
      'IT': 'Italy',
      'NL': 'Netherlands'
    };
    return countryMap[countryCode] || countryCode;
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-medium text-gray-900">Email Templates</h3>
        <button
          onClick={() => openAddModal(activeTab)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md shadow-sm hover:bg-blue-700 flex items-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          New {getTypeName(activeTab)} Template
        </button>
      </div>
      
      <p className="text-sm text-gray-600 mb-4">
        Create and manage reusable email templates with placeholders for personalization.
        Select a category below to view and manage different types of templates.
      </p>
      
      <div className="mb-4 bg-blue-50 border border-blue-200 text-sm rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-700">Country-specific templates now available</h3>
            <p className="mt-1 text-sm text-blue-600">
              Use the Country dropdown when creating or editing a template to make it country-specific.
              The system will automatically use these templates based on the recipient's country.
              <strong className="block mt-1">Supported template types:</strong>
              <ul className="list-disc pl-5 mt-1">
                <li>Course reminders (for tutors and students)</li>
                <li>Invoice reminders</li>
                <li>Payment received confirmations</li>
              </ul>
              <strong className="block mt-1">Important:</strong> Course reminders are ONLY sent for calendar events with type "course" - no other event types will receive automated reminders.
            </p>
          </div>
        </div>
      </div>
      
      {/* Template type tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-6">
          {templateTypes.length > 0 ? (
            templateTypes.map(type => (
              <button
                key={type.id}
                onClick={() => setActiveTab(type.id)}
                className={`
                  whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm
                  ${activeTab === type.id 
                    ? 'border-blue-500 text-blue-600' 
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                `}
              >
                {type.name}
                <span className="ml-2 py-0.5 px-2 bg-gray-100 text-xs rounded-full">
                  {templates.filter(t => t.template_type === type.id).length}
                </span>
              </button>
            ))
          ) : (
            <div className="py-4 px-1 text-sm text-gray-500">Loading template types...</div>
          )}
        </nav>
      </div>
      
      {activeTab !== 'manual' && (
        <div className="mb-6 bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm">
                {getTypeName(activeTab)} templates are used for automated system notifications. 
                Customize these templates to change how notifications look when sent to recipients.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
          {error}
        </div>
      )}
      
      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded">
          {success}
        </div>
      )}
      
      {loading && templates.length === 0 ? (
        <div className="animate-pulse">
          <div className="h-16 bg-gray-200 rounded w-full mb-3"></div>
          <div className="h-16 bg-gray-200 rounded w-full mb-3"></div>
          <div className="h-16 bg-gray-200 rounded w-full mb-3"></div>
        </div>
      ) : (
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
          {filteredTemplates.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <p className="text-sm">No {getTypeName(activeTab)} templates found. Create your first template to get started.</p>
              <button
                onClick={() => openAddModal(activeTab)}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md shadow-sm hover:bg-blue-700"
              >
                Create Template
              </button>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Template Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subject
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Variables
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTemplates.map((template) => (
                  <tr 
                    key={template.id} 
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={(e) => {
                      // Only if we're not clicking on action buttons
                      if (!e.target.closest('button')) {
                        openPreviewModal(template);
                      }
                    }}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900">{template.name}</div>
                      <div className="text-sm text-gray-500 flex items-center">
                        <span className="mr-2">Created by {template.created_by_name || 'System'}</span>
                        {template.country_code ? (
                          <span className="px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                            {getCountryName(template.country_code)} ({template.country_code})
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">Global</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 line-clamp-1">{template.subject}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500">
                        {template.variables && template.variables.length > 0 ? (
                          template.variables.map((variable, index) => (
                            <span key={index} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 mr-1 mb-1">
                              {`{{${variable}}}`}
                            </span>
                          ))
                        ) : (
                          <span className="text-gray-400 italic">No variables</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {template.is_default ? (
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          Default
                        </span>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent row click
                            handleMakeDefault(template.id);
                          }}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          Make Default
                        </button>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent row click
                          openEditModal(template);
                        }}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        Edit
                      </button>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent row click
                          handleDelete(template.id);
                        }}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      
      {/* Preview Modal */}
      {showPreviewModal && previewTemplate && (
        <div className="fixed z-20 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div 
              className="fixed inset-0 transition-opacity"
              aria-hidden="true"
              onClick={closePreviewModal}
            >
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        {previewTemplate.name}
                      </h3>
                      <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                        {getTypeName(previewTemplate.template_type)}
                      </span>
                    </div>
                    
                    <div className="bg-gray-50 p-4 rounded mb-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-1">Subject:</h4>
                      <p className="text-sm text-gray-900">{previewTemplate.subject}</p>
                    </div>
                    
                    <div className="border border-gray-300 rounded-md mb-4">
                      <div className="p-2 bg-gray-50 border-b border-gray-300">
                        <h4 className="text-sm font-medium text-gray-700">Email Content:</h4>
                      </div>
                      <div 
                        className="p-4 prose prose-sm max-w-none" 
                        style={{maxHeight: '400px', overflowY: 'auto'}}
                        dangerouslySetInnerHTML={{ __html: previewTemplate.body }}
                      />
                      {/* Add a note about the rich text preview */}
                      <div className="px-4 pb-2 text-xs text-gray-500">
                        This is a preview of how your email template will appear when sent.
                      </div>
                    </div>
                    
                    {previewTemplate.variables && previewTemplate.variables.length > 0 && (
                      <div className="mt-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Variables:</h4>
                        <div className="flex flex-wrap gap-1">
                          {previewTemplate.variables.map((variable, index) => (
                            <span key={index} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                              {`{{${variable}}}`}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={() => {
                    closePreviewModal();
                    openEditModal(previewTemplate);
                  }}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Edit Template
                </button>
                <button
                  onClick={closePreviewModal}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal for adding/editing templates */}
      {showModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div 
              className="fixed inset-0 transition-opacity"
              aria-hidden="true"
              onClick={closeModal}
            >
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                      {modalMode === 'add' ? 'Create New Template' : 'Edit Template'}
                    </h3>
                    
                    <form onSubmit={handleSubmit}>
                      <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                        <div className="sm:col-span-3">
                          <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                            Template Name
                          </label>
                          <div className="mt-1">
                            <input
                              type="text"
                              name="name"
                              id="name"
                              value={formData.name}
                              onChange={handleInputChange}
                              required
                              className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                            />
                          </div>
                        </div>
                        
                        <div className="sm:col-span-3">
                          <label htmlFor="template_type" className="block text-sm font-medium text-gray-700">
                            Template Type
                          </label>
                          <div className="mt-1">
                            <select
                              name="template_type"
                              id="template_type"
                              value={formData.template_type}
                              onChange={handleInputChange}
                              disabled={modalMode === 'edit'} // Can't change type of existing template
                              className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                            >
                              {templateTypes.map(type => (
                                <option key={type.id} value={type.id}>
                                  {type.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <p className="mt-1 text-xs text-gray-500">
                            {modalMode === 'edit' ? 'Template type cannot be changed after creation.' : 'Choose the type of template you want to create.'}
                          </p>
                        </div>
                        
                        <div className="sm:col-span-3">
                          <label htmlFor="country_code" className="block text-sm font-medium text-gray-700">
                            Country (Optional)
                          </label>
                          <div className="mt-1">
                            <select
                              name="country_code"
                              id="country_code"
                              value={formData.country_code || ''}
                              onChange={handleInputChange}
                              className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                            >
                              <option value="">Global (Default)</option>
                              <option value="NO">Norway (NO)</option>
                              <option value="SE">Sweden (SE)</option>
                              <option value="DK">Denmark (DK)</option>
                              <option value="FI">Finland (FI)</option>
                              <option value="DE">Germany (DE)</option>
                              <option value="GB">United Kingdom (GB)</option>
                              <option value="US">United States (US)</option>
                              <option value="FR">France (FR)</option>
                              <option value="ES">Spain (ES)</option>
                              <option value="IT">Italy (IT)</option>
                              <option value="NL">Netherlands (NL)</option>
                            </select>
                          </div>
                          <p className="mt-1 text-xs text-gray-500">
                            Select a country to make this template country-specific. Leave empty for a global template.
                          </p>
                        </div>
                        
                        <div className="sm:col-span-6">
                          <label htmlFor="subject" className="block text-sm font-medium text-gray-700">
                            Email Subject
                          </label>
                          <div className="mt-1">
                            <input
                              type="text"
                              name="subject"
                              id="subject"
                              value={formData.subject}
                              onChange={handleInputChange}
                              required
                              className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                              placeholder="e.g. Welcome to {`{{company_name}}`}"
                            />
                          </div>
                          <p className="mt-1 text-xs text-gray-500">
                            You can use variables like {`{{variable_name}}`} that will be replaced when sending emails.
                          </p>
                        </div>
                        
                        <div className="sm:col-span-6">
                          <div className="flex justify-between items-center mb-1">
                            <label htmlFor="body" className="block text-sm font-medium text-gray-700">
                              Email Body
                            </label>
                            <div className="bg-gray-100 rounded-md p-0.5 flex items-center">
                              <button
                                type="button"
                                onClick={() => setShowHtmlSource(false)}
                                className={`text-xs px-3 py-1 rounded-md ${!showHtmlSource ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-700'}`}
                              >
                                Rich Text
                              </button>
                              <button
                                type="button"
                                onClick={() => setShowHtmlSource(true)}
                                className={`text-xs px-3 py-1 rounded-md ${showHtmlSource ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-700'}`}
                              >
                                HTML
                              </button>
                            </div>
                          </div>
                          <div className="mt-1">
                            <div className="border border-gray-300 rounded-md overflow-hidden">
                              <div className="editor-container">
                                <ReactQuill
                                  id="body"
                                  value={formData.body}
                                  onChange={(content) => setFormData(prev => ({ ...prev, body: content }))}
                                  modules={modules}
                                  formats={formats}
                                  placeholder={`Dear {{lead_name}},\n\nThank you for your interest in {{company_name}}...`}
                                  className="bg-white"
                                  theme="snow"
                                />
                              </div>
                              <style jsx>{`
                                /* Local styles for Quill editor */
                                .editor-container .ql-container {
                                  font-size: 0.875rem;
                                  font-family: inherit;
                                  min-height: 250px;
                                  max-height: 400px;
                                  overflow-y: auto;
                                }
                                .editor-container .ql-toolbar {
                                  background-color: #f9fafb;
                                  border-bottom: 1px solid #e5e7eb;
                                }
                                .editor-container .ql-toolbar button {
                                  color: #4b5563;
                                }
                                .editor-container .ql-toolbar button:hover {
                                  color: #2563eb;
                                }
                                .editor-container .ql-toolbar button.ql-active,
                                .editor-container .ql-toolbar .ql-picker-label.ql-active {
                                  color: #2563eb;
                                }
                                .editor-container .ql-toolbar .ql-picker-options {
                                  background-color: white;
                                  border-color: #e5e7eb;
                                }
                                .editor-container .ql-snow .ql-editor pre.ql-syntax {
                                  font-family: monospace;
                                  background-color: #f3f4f6;
                                  color: #1f2937;
                                }
                              `}</style>
                            </div>
                            <div className="h-10">{/* Extra space for editor toolbar */}</div>
                          </div>
                          <div className="flex items-center mt-3">
                            <div className="text-xs text-gray-700 mr-2">Insert variable:</div>
                            <div className="relative">
                              <select
                                className="block w-full pl-3 pr-10 py-1 text-xs border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md"
                                onChange={(e) => {
                                  if (e.target.value) {
                                    insertVariable(e.target.value);
                                    e.target.value = ''; // Reset after insertion
                                  }
                                }}
                                value=""
                              >
                                <option value="" disabled>Choose variable...</option>
                                {commonVariables.map(variable => (
                                  <option key={variable} value={variable}>{`{{${variable}}}`}</option>
                                ))}
                                {formData.variables
                                  .filter(v => !commonVariables.includes(v))
                                  .map(variable => (
                                    <option key={variable} value={variable}>{`{{${variable}}}`}</option>
                                  ))
                                }
                              </select>
                            </div>
                            <p className="text-xs text-gray-500 ml-3">
                              Use the formatting toolbar to style your email content.
                            </p>
                          </div>
                          
                          {showHtmlSource && (
                            <div className="mt-3">
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                HTML Source:
                              </label>
                              <textarea
                                id="html-source"
                                value={formData.body}
                                onChange={(e) => setFormData(prev => ({ ...prev, body: e.target.value }))}
                                className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md font-mono text-xs"
                                rows="6"
                              />
                              <p className="mt-1 text-xs text-gray-500">
                                Warning: Editing HTML directly may affect formatting. Use with caution.
                              </p>
                            </div>
                          )}
                        </div>
                        
                        <div className="sm:col-span-6">
                          <label htmlFor="variables" className="block text-sm font-medium text-gray-700">
                            Template Variables
                          </label>
                          <div className="mt-1">
                            <input
                              type="text"
                              id="variables"
                              value={formData.variables.join(', ')}
                              onChange={handleVariablesChange}
                              className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                              placeholder="lead_name, company_name, user_name"
                            />
                          </div>
                          <p className="mt-1 text-xs text-gray-500">
                            Comma-separated list of variable names used in this template. These will be shown to users when selecting this template.
                          </p>
                          
                          {/* Show available variables based on template type */}
                          {formData.template_type === 'course_reminder_tutor' && (
                            <div className="mt-3 bg-gray-50 p-3 rounded border border-gray-200">
                              <p className="text-xs font-medium text-gray-700 mb-1">Available variables for tutor reminders:</p>
                              <div className="flex flex-wrap gap-1">
                                {['event.title', 'event.date', 'event.time', 'event.location', 'event.description', 
                                  'event.course_name', 'event.course_level', 'event.course_age_group', 'event.country',
                                  'tutor.name', 'students.length', 'students.list'].map(variable => (
                                  <span key={variable} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                    {`{{${variable}}}`}
                                  </span>
                                ))}
                              </div>
                              <div className="mt-2 px-3 py-2 bg-yellow-50 text-yellow-700 text-xs border border-yellow-200 rounded">
                                <strong>Country-specific templates:</strong> Include a country name in the template name (e.g., "Tutor Reminder - Norway") 
                                to automatically send this template for course events in that country. The system will match event.country with your template name.
                                <strong className="block mt-1">Note:</strong> This only applies to events with type "course".
                              </div>
                            </div>
                          )}
                          
                          {formData.template_type === 'course_reminder_student' && (
                            <div className="mt-3 bg-gray-50 p-3 rounded border border-gray-200">
                              <p className="text-xs font-medium text-gray-700 mb-1">Available variables for student reminders:</p>
                              <div className="flex flex-wrap gap-1">
                                {['event.title', 'event.date', 'event.time', 'event.location', 'event.description', 
                                  'event.course_name', 'event.country', 'tutor.name', 'student.name', 'student.student_name'].map(variable => (
                                  <span key={variable} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                    {`{{${variable}}}`}
                                  </span>
                                ))}
                              </div>
                              <div className="mt-2 px-3 py-2 bg-yellow-50 text-yellow-700 text-xs border border-yellow-200 rounded">
                                <strong>Country-specific templates:</strong> Include a country name in the template name (e.g., "Student Reminder - Germany") 
                                to automatically send this template for course events in that country. The system will match event.country with your template name.
                                <strong className="block mt-1">Note:</strong> This only applies to events with type "course".
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <div className="sm:col-span-6">
                          <div className="flex items-start">
                            <div className="flex items-center h-5">
                              <input
                                id="is_default"
                                name="is_default"
                                type="checkbox"
                                checked={formData.is_default}
                                onChange={handleInputChange}
                                className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                              />
                            </div>
                            <div className="ml-3 text-sm">
                              <label htmlFor="is_default" className="font-medium text-gray-700">Set as default template</label>
                              <p className="text-gray-500">This template will be selected by default when composing new emails.</p>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-5 sm:mt-6 sm:flex sm:flex-row-reverse">
                        <button
                          type="submit"
                          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                        >
                          {loading ? 'Saving...' : modalMode === 'add' ? 'Create Template' : 'Update Template'}
                        </button>
                        <button
                          type="button"
                          onClick={closeModal}
                          className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDelete}
        message="Are you sure you want to delete this template? This action cannot be undone."
      />
    </div>
  );
};

export default EmailTemplatesSettings;